import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
} from "react";

import {
  closestCenter,
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToHorizontalAxis } from "@dnd-kit/modifiers";
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
  useSortable,
} from "@dnd-kit/sortable";
import { Table, Breadcrumb } from "antd";

import moment from "moment-timezone";
import { format } from "date-fns";
import { CalendarIcon, CheckIcon } from "@heroicons/react/16/solid";
import { cn } from "../../lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/popover";

import { Button } from "../../components/ui/button";
import { Calendar } from "../../components/ui/calendar";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../../components/ui/command";
import { ChevronsUpDown, Download, RefreshCw } from "lucide-react";
import {
  get_call_logs,
  get_data_to_download,
} from "../../api/CustomerPortalApi";
import { list_all_languages } from "../../api/GeneralApi";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { formatCustomerCountry } from "../../utilities/formatCustomerCountry";

const DragIndexContext = createContext({ active: -1, over: -1 });
const MAX_RANGE_DAYS = 180;

const formatDate = (dateString, timezone) => {
  if (!dateString) return "-";
  return moment.utc(dateString).tz(timezone).format("YYYY-MM-DD HH:mm:ss");
};

const getEndDateForFilter = (date) => {
  if (date?.to && moment(date.to).isValid()) {
    return moment(date.to).format("YYYY-MM-DD");
  }
  if (date?.from && moment(date.from).isValid()) {
    return moment(date.from).format("YYYY-MM-DD");
  }
  return null;
};

const getClickedDateWhenRangeExceeded = (newDate, previousDate) => {
  const prevFromStr = previousDate?.from
    ? moment(previousDate.from).format("YYYY-MM-DD")
    : null;

  const newFromStr = moment(newDate.from).format("YYYY-MM-DD");
  const newToStr = moment(newDate.to).format("YYYY-MM-DD");

  if (prevFromStr && newToStr !== prevFromStr) {
    return newDate.to;
  }
  if (prevFromStr && newFromStr !== prevFromStr) {
    return newDate.from;
  }

  return newDate.to ?? newDate.from;
};

const buildReportFilters = (date, timezone, selectedLanguages, pagination) => {
  const filters = {};

  if (date?.from && moment(date.from).isValid()) {
    filters.start_date = moment
      .tz(moment(date.from).format("YYYY-MM-DD"), timezone)
      .startOf("day")
      .utc()
      .format("YYYY-MM-DD HH:mm:ss");
  }

  const endDay = getEndDateForFilter(date);
  if (endDay) {
    filters.end_date = moment
      .tz(endDay, timezone)
      .add(1, "day")
      .startOf("day")
      .utc()
      .format("YYYY-MM-DD HH:mm:ss");
  }

  if (selectedLanguages.length > 0) {
    filters.languages = selectedLanguages.join(",");
  }

  if (pagination) {
    filters.page = pagination.current;
    filters.page_size = pagination.pageSize;
  }

  return filters;
};

const dragActiveStyle = (dragState, id) => {
  const { active, over, direction } = dragState;
  let style = {};
  if (active && active === id) {
    style = { backgroundColor: "gray", opacity: 0.5 };
  } else if (over && id === over && active !== over) {
    style =
      direction === "right"
        ? { borderRight: "1px dashed gray" }
        : { borderLeft: "1px dashed gray" };
  }
  return style;
};

const TableBodyCell = (props) => {
  const dragState = useContext(DragIndexContext);
  return (
    <td
      {...props}
      style={Object.assign(
        Object.assign({}, props.style),
        dragActiveStyle(dragState, props.id)
      )}
    >
      {props.children}
    </td>
  );
};

const TableHeaderCell = (props) => {
  const dragState = useContext(DragIndexContext);
  const { attributes, listeners, setNodeRef, isDragging } = useSortable({
    id: props.id,
  });
  const style = Object.assign(
    Object.assign(
      Object.assign(Object.assign({}, props.style), { cursor: "move" }),
      isDragging
        ? { position: "relative", zIndex: 9999, userSelect: "none" }
        : {}
    ),
    dragActiveStyle(dragState, props.id)
  );
  return (
    <th
      {...props}
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
    >
      {props.children}
    </th>
  );
};

const extractAttributeValue = (row, attribute) => {
  if (
    row &&
    row.attributes &&
    typeof row.attributes === "object" &&
    !Array.isArray(row.attributes)
  ) {
    const value = row.attributes[attribute.attribute_id];
    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }

  if (row && Array.isArray(row.attributes)) {
    const found = row.attributes.find(
      (attr) =>
        attr.attribute_id === attribute.attribute_id ||
        attr.attribute_name === attribute.attribute_name
    );
    if (found) {
      return found.attribute_value !== null &&
        found.attribute_value !== undefined
        ? found.attribute_value
        : "-";
    }
  }

  if (row && row.attributeValues && typeof row.attributeValues === "object") {
    const value = row.attributeValues[attribute.attribute_id];
    if (value !== undefined && value !== null) {
      return value;
    }
  }

  return "-";
};

const CallsReport = () => {
  const [timezone, setTimezone] = useState("America/New_York");

  const getTodayDateRange = (tz = timezone) => ({
    from: moment.tz(moment().format("YYYY-MM-DD"), tz).startOf("day").toDate(),
    to: moment.tz(moment().format("YYYY-MM-DD"), tz).startOf("day").toDate(),
  });

  const [date, setDate] = useState(() => getTodayDateRange("America/New_York"));
  const [isDownloading, setIsDownloading] = useState(false);
  const [dragIndex, setDragIndex] = useState({ active: -1, over: -1 });
  const [languageOpen, setLanguageOpen] = useState(false);
  const [selectedLanguages, setSelectedLanguages] = useState([]);
  const [apiLoading, setApiLoading] = useState(false);
  const [callLogs, setCallLogs] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 30,
    total: 0,
  });
  const [attributesList, setAttributesList] = useState([]);
  const [languages, setLanguages] = useState([]);

  const handleManualRefresh = async () => {
    setRefreshing(true);
    try {
      await fetchCallLogs();
    } finally {
      setTimeout(() => {
        setRefreshing(false);
      }, 500);
    }
  };

  const clearAllFilters = () => {
    setDate(getTodayDateRange());
    setSelectedLanguages([]);
  };

  const fetchCallLogs = useCallback(async () => {
    setApiLoading(true);
    try {
      const filters = buildReportFilters(
        date,
        timezone,
        selectedLanguages,
        pagination
      );
      const response = await get_call_logs(filters);

      const allCustomerAttributes =
        response.data.attribute_definitions || response.data.attributes || [];

      const attributeValuesByEngagement = {};
      const attributeValues =
        response.data.attribute_values || response.data.attributes || [];

      attributeValues.forEach((attr) => {
        if (attr.engagement_id) {
          if (!attributeValuesByEngagement[attr.engagement_id]) {
            attributeValuesByEngagement[attr.engagement_id] = {};
          }
          attributeValuesByEngagement[attr.engagement_id][attr.attribute_id] =
            attr.attribute_value;
        }
      });

      const callLogsWithAttributes = (response.data.items || []).map(
        (callLog) => ({
          ...callLog,
          attributeValues:
            attributeValuesByEngagement[callLog.engagement_id] || {},
        })
      );

      setCallLogs(callLogsWithAttributes);
      setAttributesList(allCustomerAttributes);
      setPagination((prev) => ({
        ...prev,
        total: response.data.meta.total || 0,
      }));
    } catch (error) {
      console.error("Error fetching call logs:", error);
    } finally {
      setApiLoading(false);
    }
  }, [
    date,
    selectedLanguages,
    pagination.current,
    pagination.pageSize,
  ]);

  useEffect(() => {
    if (apiLoading) return;
    const pollingInterval = setInterval(() => {
      fetchCallLogs();
    }, 60000 * 15);
    return () => clearInterval(pollingInterval);
  }, [fetchCallLogs, apiLoading]);

  useEffect(() => {
    fetchCallLogs();
  }, [fetchCallLogs]);

  useEffect(() => {
    const loadLanguages = async () => {
      const response = await list_all_languages();
      setLanguages(
        Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response)
            ? response
            : []
      );
    };
    loadLanguages();
  }, []);

  const baseColumns = useMemo(
    () => [
      {
        title: "Start Time",
        dataIndex: "engagement_start_ts",
        width: 100,
        fixed: "left",
        render: (text) => formatDate(text, timezone),
      },
      { title: "Caller", dataIndex: "caller_number", width: 100 },
      { title: "ANI", dataIndex: "dialed_number", width: 100 },
      { title: "Access Code", dataIndex: "access_code", width: 70 },
      {
        title: "Country",
        dataIndex: "customer_country",
        width: 100,
        render: (text) => formatCustomerCountry(text),
      },
      { title: "Department", dataIndex: "category_code_value", width: 100 },
      { title: "Language", dataIndex: "language", width: 100 },
      ...attributesList.map((attribute) => ({
        title: attribute.attribute_name,
        dataIndex: `attribute_${attribute.attribute_id}`,
        width: 100,
        render: (_, row) => extractAttributeValue(row, attribute),
      })),
      {
        title: "Interpreter ID",
        dataIndex: "interpreter_answered_s_id",
        width: 100,
      },
      {
        title: "Duration (sec)",
        dataIndex: "interpretation_duration_s",
        width: 60,
        render: (text) => (text ? parseFloat(text).toFixed(1) : "0"),
      },
      {
        title: "Bill Rate",
        dataIndex: "customer_bill_rate",
        width: 60,
        render: (text) => (text ? `$${parseFloat(text).toFixed(2)}` : "-"),
      },
      {
        title: "Total Cost",
        dataIndex: "customer_bill",
        width: 60,
        render: (text) => (text ? `$${parseFloat(text).toFixed(2)}` : "-"),
      },
      { title: "Channel", dataIndex: "channel", width: 70 },
    ],
    [attributesList, timezone]
  );

  const withKeysAndDnDProps = useCallback((cols) => {
    return cols.map((column, i) => ({
      ...column,
      key: `${i}`,
      onHeaderCell: () => ({ id: `${i}` }),
      onCell: () => ({ id: `${i}` }),
    }));
  }, []);

  const [columns, setColumns] = useState(() =>
    withKeysAndDnDProps(baseColumns)
  );

  useEffect(() => {
    setColumns(withKeysAndDnDProps(baseColumns));
  }, [baseColumns, withKeysAndDnDProps]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 1,
      },
    })
  );

  const onDragEnd = ({ active, over }) => {
    if (active.id !== over?.id) {
      setColumns((prevState) => {
        const activeIndex = prevState.findIndex((i) => i.key === active?.id);
        const overIndex = prevState.findIndex((i) => i.key === over?.id);
        return arrayMove(prevState, activeIndex, overIndex);
      });
    }
    setDragIndex({ active: -1, over: -1 });
  };

  const onDragOver = ({ active, over }) => {
    const activeIndex = columns.findIndex((i) => i.key === active.id);
    const overIndex = columns.findIndex((i) => i.key === over?.id);
    setDragIndex({
      active: active.id,
      over: over?.id,
      direction: overIndex > activeIndex ? "right" : "left",
    });
  };

  const downloadCSV = async () => {
    setIsDownloading(true);

    try {
      const filters = buildReportFilters(date, timezone, selectedLanguages);
      const response = await get_data_to_download(filters, () => {});

      const visibleColumns = columns;
      const header = visibleColumns.map((col) => `"${col.title}"`).join(",");

      const formattedData = response.data.map((item) => ({
        ...item,
        engagement_start_ts: formatDate(item.engagement_start_ts, timezone),
        engagement_end_ts: formatDate(item.engagement_end_ts, timezone),
        customer_country: formatCustomerCountry(item.customer_country),
      }));

      const csvRows = formattedData.map((row) => {
        return visibleColumns
          .map((column) => {
            let value;

            if (column.dataIndex.startsWith("attribute_") && column.render) {
              value = column.render(null, row);
            } else {
              value = row[column.dataIndex];
            }

            return `"${value ? String(value).replace(/"/g, '""') : ""}"`;
          })
          .join(",");
      });

      const csvContent = [header, ...csvRows].join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `calls_report_${format(new Date(), "yyyy-MM-dd_HH-mm")}.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error downloading CSV:", error);
    } finally {
      setIsDownloading(false);
    }
  };

  const activeDragColumn = columns.find((i) => i.key === dragIndex.active);

  return (
    <div>
      <Breadcrumb
        className="!mb-6"
        items={[
          {
            title: "Home",
          },
          {
            title: "Customer Call Logs",
          },
        ]}
      />
      <div className="grid lg:grid-cols-2 xl:grid-cols-4 sm:grid-cols-2 grid-cols-1 gap-10 mb-10">
        <div className="flex flex-col">
          <label className="text-sm text-muted-foreground mb-1">
            Date Range{" "}
            <span className="text-xs text-gray-400">
              (max {MAX_RANGE_DAYS} days)
            </span>
          </label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                id="date"
                variant={"outline"}
                className={cn(
                  "justify-start text-left font-normal cursor-pointer ",
                  !date && "text-muted-foreground"
                )}
              >
                <CalendarIcon />
                {date?.from ? (
                  date.to ? (
                    <>
                      {format(date.from, "LLL dd, y")} -{" "}
                      {format(date.to, "LLL dd, y")}
                    </>
                  ) : (
                    format(date.from, "LLL dd, y")
                  )
                ) : (
                  <span>Pick a date</span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-1 z-50 bg-white" align="center">
              <Calendar
                initialFocus
                mode="range"
                defaultMonth={date?.from}
                selected={date}
                onSelect={(newDate) => {
                  if (!newDate) {
                    setDate(getTodayDateRange());
                    return;
                  }

                  if (!newDate.from) {
                    setDate(getTodayDateRange());
                    return;
                  }

                  if (newDate.to && moment(newDate.to).isValid()) {
                    const daysDifference = Math.abs(
                      moment(newDate.to).diff(moment(newDate.from), "days")
                    );
                    if (daysDifference > MAX_RANGE_DAYS) {
                      const clickedDate = getClickedDateWhenRangeExceeded(
                        newDate,
                        date
                      );
                      const resetFrom = moment
                        .tz(
                          moment(clickedDate).format("YYYY-MM-DD"),
                          timezone
                        )
                        .startOf("day")
                        .toDate();
                      setDate({ from: resetFrom, to: undefined });
                      return;
                    }
                  }

                  const fromDate = moment
                    .tz(moment(newDate.from).format("YYYY-MM-DD"), timezone)
                    .startOf("day")
                    .toDate();

                  let toDate;
                  if (newDate.to && moment(newDate.to).isValid()) {
                    toDate = moment
                      .tz(moment(newDate.to).format("YYYY-MM-DD"), timezone)
                      .startOf("day")
                      .toDate();
                  } else {
                    toDate = moment
                      .tz(moment(newDate.from).format("YYYY-MM-DD"), timezone)
                      .endOf("day")
                      .toDate();
                  }

                  setDate({
                    from: fromDate,
                    to: toDate,
                  });
                }}
                numberOfMonths={1}
              />
            </PopoverContent>
          </Popover>
        </div>

        <div className="flex flex-col">
          <label className="text-sm text-muted-foreground mb-1">
            Languages
          </label>
          <Popover open={languageOpen} onOpenChange={setLanguageOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={languageOpen}
                className="justify-between cursor-pointer"
              >
                <span className="line-clamp-1">
                  {selectedLanguages.length
                    ? `${selectedLanguages.length} selected`
                    : "Select language..."}
                </span>
                <ChevronsUpDown className="opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="p-0">
              <Command>
                <CommandInput placeholder="Search language..." />
                <CommandList>
                  <CommandEmpty>No Languages found.</CommandEmpty>
                  <CommandGroup>
                    {languages.map((lang) => (
                      <CommandItem
                        key={lang.name}
                        value={lang.name}
                        className={"cursor-pointer"}
                        onSelect={() => {
                          setSelectedLanguages((prev) =>
                            prev.includes(lang.name)
                              ? prev.filter((v) => v !== lang.name)
                              : [...prev, lang.name]
                          );
                        }}
                      >
                        {lang.name}
                        <CheckIcon
                          className={`ml-auto ${
                            selectedLanguages.includes(lang.name)
                              ? "opacity-100"
                              : "opacity-0"
                          }`}
                        />
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          <div className="mt-1 gap-1 flex flex-row flex-wrap max-h-20 overflow-y-auto items-start justify-start content-start">
            {selectedLanguages.map((langCode) => {
              const language = languages.find((l) => l.code === langCode);
              const langName = language ? language.name : langCode;
              return (
                <span
                  key={langCode}
                  className="inline-flex items-center gap-x-0.5 rounded-md bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600"
                >
                  {langName}
                  <button
                    type="button"
                    className="group relative -mr-1 size-3.5 rounded-xs hover:bg-gray-500/20"
                    onClick={() =>
                      setSelectedLanguages((prev) =>
                        prev.filter((lang) => lang !== langCode)
                      )
                    }
                  >
                    <span className="sr-only">Remove</span>
                    <svg
                      viewBox="0 0 14 14"
                      className="size-3.5 stroke-gray-700/50 group-hover:stroke-gray-700/75"
                    >
                      <path d="M4 4l6 6m0-6l-6 6" />
                    </svg>
                    <span className="absolute -inset-1" />
                  </button>
                </span>
              );
            })}
          </div>
        </div>
      </div>
      <h1 className="text-2xl font-bold">Customer Report</h1>
      <div className="flex justify-between items-end mb-4">
        <div className="flex space-x-2 justify-end w-full items-end">
          <div className="flex space-x-2 items-end">
            <div className="flex flex-col space-x-2">
              <span className="text-sm text-gray-600">Timezone:</span>
              <Select value={timezone} onValueChange={setTimezone}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Select timezone" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="America/New_York">
                    Eastern Time (EST)
                  </SelectItem>
                  <SelectItem value="UTC">UTC</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              variant="outline"
              className="flex items-center gap-2 cursor-pointer border-red-500 text-red-500 hover:bg-red-50"
              onClick={clearAllFilters}
            >
              Clear Filters
            </Button>

            <Button
              variant="outline"
              className="flex items-center gap-2 cursor-pointer"
              onClick={handleManualRefresh}
              disabled={refreshing}
            >
              <RefreshCw
                className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`}
              />
              Refresh
            </Button>
            <Button
              variant="outline"
              className="flex items-center gap-2 cursor-pointer"
              onClick={downloadCSV}
              disabled={isDownloading}
            >
              <Download className="h-4 w-4" />
              {isDownloading ? "Downloading..." : "Download CSV"}
            </Button>
          </div>
        </div>
      </div>

      <DndContext
        sensors={sensors}
        modifiers={[restrictToHorizontalAxis]}
        onDragEnd={onDragEnd}
        onDragOver={onDragOver}
        collisionDetection={closestCenter}
      >
        <SortableContext
          items={columns.map((i) => i.key)}
          strategy={horizontalListSortingStrategy}
        >
          <DragIndexContext.Provider value={dragIndex}>
            <Table
              rowHoverable={false}
              size="small"
              scroll={{ x: "max-content", y: "calc(100vh - 500px)" }}
              bordered
              rowKey="engagement_id"
              columns={columns}
              dataSource={callLogs}
              pagination={{
                ...pagination,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total) => `${total} records`,
                pageSizeOptions: ["10", "20", "30", "50", "100"],
                size: "default",
                position: ["bottomRight"],
                defaultPageSize: 30,
                current: pagination.current,
                pageSize: pagination.pageSize,
                onChange: (page, pageSize) => {
                  setPagination((prev) => ({
                    ...prev,
                    current: page,
                    pageSize: pageSize,
                  }));
                },
                onShowSizeChange: (current, size) => {
                  setPagination((prev) => ({
                    ...prev,
                    current: 1,
                    pageSize: size,
                  }));
                },
              }}
              components={{
                header: { cell: TableHeaderCell },
                body: { cell: TableBodyCell },
              }}
            />
          </DragIndexContext.Provider>
        </SortableContext>
        <DragOverlay>
          <th style={{ backgroundColor: "gray", padding: 16 }}>
            {activeDragColumn?.title}
          </th>
        </DragOverlay>
      </DndContext>
    </div>
  );
};

export default CallsReport;
